import { mdiStarOutline, mdiTuneVariant, mdiWrenchOutline } from '@mdi/js'

/**
 * INSTRUÇÕES:
 *
 * Changelog interno do Minha Escala.
 *
 * As entradas sao CURADAS manualmente por versao — nao sao geradas de tags/commits —
 * porque muitos deploys sao triviais e nao interessam ao usuario final.
 * Escreva cada item em linguagem de beneficio ("o que o usuario ganha"), nao de implementacao.
 *
 * Ao publicar uma release relevante: adicione uma nova entrada NO TOPO do array.
 */

export const changelog: ChangelogEntry[] = [
  {
    version: '1.10.813',
    date: '2026-10-22',
    range: 'v1.10.752...v1.10.813',
    summary:
      'Revisão de documentos dos profissionais, estorno de excepcionalidades pelo fluxo de aprovação, registro de plantão mais guiado, comunicados com confirmação de leitura e abertura de chamados de suporte pelo menu.',
    items: [
      {
        type: 'feature',
        title: 'Revisão de documentos dos profissionais',
        description:
          'Nova fila para revisar os documentos enviados pelos médicos, com uma pasta de documentos por profissional e requisitos de qualificação. O card da fila mostra há quanto tempo o documento mais antigo está esperando.'
      },
      {
        type: 'feature',
        title: 'Estorno de excepcionalidades com aprovação',
        description:
          'Agora é possível pedir o estorno de uma excepcionalidade, e o pedido passa pelo fluxo de aprovação. A linha do tempo mostra as justificativas de cada decisão, as justificativas de todos os itens são mantidas em uma rejeição e os cards do resumo filtram a lista por status.'
      },
      {
        type: 'feature',
        title: 'Registro de plantão mais guiado',
        description:
          'O envio da folha já vem com o turno do plantão selecionado, avisa se a folha daquele turno já foi enviada e mostra alertas de qualidade antes do envio. Folhas rejeitadas podem ser reenviadas como uma nova folha, o histórico de folhas rejeitadas aparece no detalhe do registro e o auditor pode aprovar uma folha com linhas sem correspondência após confirmar.'
      },
      {
        type: 'feature',
        title: 'Comunicados e pesquisa NPS do plantão',
        description:
          'Comunicados com confirmação de leitura e um formulário público de NPS para avaliar o plantão.'
      },
      {
        type: 'feature',
        title: 'Chamado de suporte pelo menu do perfil',
        description:
          'Abra um chamado de suporte direto do menu do seu perfil, sem sair da tela em que está.'
      },
      {
        type: 'feature',
        title: 'Mais filtros e ordenação',
        description:
          'A lista de unidades ganhou abas Todas, Ativas e Inativas com contadores e um seletor de ordenação. Os pagamentos prioritários mostram e filtram por unidade, setor e cargo, e o relatório de excepcionalidades exibe a justificativa do aprovador.'
      },
      {
        type: 'feature',
        title: 'Recorrência quinzenal e escala em etapas nomeadas',
        description:
          'Plantões podem ser criados com recorrência quinzenal, e o assistente de criação de escala mostra o nome de cada etapa.'
      },
      {
        type: 'improvement',
        title: 'Painel mais leve',
        description:
          'O painel evita carregar escalas e unidades repetidas e pausa as atualizações quando a aba está em segundo plano. Os detalhes do plantão mostram as regras de pagamento e a checagem pelo app em cards.'
      },
      {
        type: 'fix',
        title: 'Erros explicados em mais telas',
        description:
          'Plantões, políticas, NPS, passagem de plantão, biometria facial, notificações, cadastro público e importação de qualificação passaram a mostrar o motivo real de uma falha. Quando as permissões não carregam, aparece uma tela para tentar de novo.'
      },
      {
        type: 'fix',
        title: 'Ajustes gerais',
        description:
          'Criar um único plantão depois de desligar a repetição volta a funcionar, as datas de validade do NPS respeitam o dia local, os nomes dos médicos aparecem no formato correto e os títulos das páginas de vagas e demanda aparecem no cabeçalho.'
      }
    ]
  },
  {
    version: '1.10.752',
    date: '2026-10-07',
    range: 'v1.10.688...v1.10.752',
    summary:
      'Painel de plantões no celular, mapa da unidade com modo TV, central de vagas renovada, nova árvore de permissões e mensagens de erro mais claras em todo o sistema.',
    items: [
      {
        type: 'feature',
        title: 'Painel de plantões no celular',
        description:
          'O painel agora tem um layout próprio para celular, com grade em níveis de zoom, resumo do mês, visões de semana e mês e seleção em lote. Ver, criar e editar plantões abre em tela cheia, e a visão clássica continua disponível.'
      },
      {
        type: 'feature',
        title: 'Mapa da unidade e modo TV',
        description:
          'Nova aba de mapa da unidade no painel, com um modo TV para deixar a cobertura dos plantões sempre visível num monitor e um link pronto para abrir na TV.'
      },
      {
        type: 'feature',
        title: 'Central de vagas renovada',
        description:
          'Filtros e cartões foram redesenhados, e um novo assistente ajuda a alocar médicos nas vagas abertas.'
      },
      {
        type: 'feature',
        title: 'Nova árvore de permissões dos usuários',
        description:
          'A tela de permissões ganhou busca, filtros, os botões expandir/recolher tudo e selos que mostram de onde vem cada permissão (perfil, grupo ou direta), com explicações ao passar o mouse.'
      },
      {
        type: 'feature',
        title: 'Portal do afiliado Antecipe',
        description:
          'Novo portal para afiliados Antecipe, com os resultados separados por competência e os pacotes consolidados.'
      },
      {
        type: 'feature',
        title: 'Melhorias no registro de plantão e na passagem de plantão',
        description:
          'A fila do registro de plantão mostra o prazo de auditoria (SLA), e recebedores avançados podem reverter uma rejeição, que fica registrada no histórico. Na passagem de plantão, o filtro de escalas respeita a unidade escolhida, existe a opção de antecedência zero e o filtro de status inclui "cancelado".'
      },
      {
        type: 'feature',
        title: 'Aviso de nova versão',
        description:
          'Quando uma nova versão do painel é publicada, você recebe um aviso para atualizar a página.'
      },
      {
        type: 'improvement',
        title: 'Mais segurança ao excluir escalas',
        description:
          'Antes de excluir uma escala, aparece um resumo dos plantões futuros que serão afetados. O resumo de exceções também passa a mostrar o valor total solicitado.'
      },
      {
        type: 'fix',
        title: 'Mensagens de erro mais claras',
        description:
          'Telas de usuários, unidades, médicos, políticas, qualificação, relatórios, NPS e login passaram a mostrar o motivo real de uma falha, em vez de uma mensagem genérica ou de uma lista vazia.'
      },
      {
        type: 'fix',
        title: 'Ajustes no painel, no NPS e na passagem de plantão',
        description:
          'Os filtros do painel foram agrupados atrás de um botão, a grade aparece em telas médias e plantões com trechos travados não podem mais ser editados nem excluídos. A avaliação NPS agora é enviada para a unidade certa e os nomes das unidades aparecem completos no ranking. O filtro de aprovações da passagem de plantão carrega todas as unidades. Depois de editar um plantão, você volta para os detalhes dele.'
      }
    ]
  },
  {
    version: '1.10.688',
    date: '2026-09-28',
    range: 'v1.10.646...v1.10.688',
    summary:
      'Nova fila de pagamentos prioritários, estado de cobertura não identificada nos plantões, renovação do registro de plantão no pós-plantão e mais filtros nos relatórios.',
    items: [
      {
        type: 'feature',
        title: 'Fila de pagamentos prioritários',
        description:
          'Nova fila dedicada para pagamentos prioritários, com contagem regressiva de SLA, abas e agrupamento, visão kanban como padrão, exportação em chip e prazo do próximo ciclo.'
      },
      {
        type: 'feature',
        title: 'Estado de cobertura não identificada nos plantões',
        description:
          'Plantões sem cobertura identificada agora têm um estado próprio no painel, com rótulos de ação mais claros e trava para a opção de "não identificado".'
      },
      {
        type: 'feature',
        title: 'Registro de plantão renovado no pós-plantão',
        description:
          'As etapas de equivalência e revisão foram redesenhadas com campos mais objetivos, e o campo de categoria da folha é ocultado quando o registro é feito pelo aplicativo. Foi adicionado um modal de confirmação para substituir o turno já registrado, exportação completa da fila de registros e acesso direto para o perfil de extração. Recebedores avançados também podem reverter um registro já aprovado.'
      },
      {
        type: 'feature',
        title: 'Novos filtros e colunas nos relatórios',
        description:
          'Os relatórios ganharam filtro e coluna por grupo operacional, além de filtro por plantões com exceção.'
      },
      {
        type: 'fix',
        title: 'Mais estabilidade na auditoria de plantões',
        description:
          'A auditoria do pós-plantão corrige a recarga da tela ao navegar entre itens da fila, atualiza o detalhe quando uma decisão é recusada, evita respostas atrasadas da fila e mantém os filtros sincronizados com a URL. Também ocultamos o campo de amostragem quando a aprovação automática está desativada, ajustamos os rótulos e a obrigatoriedade de campos no toggle "não é da nossa equipe", corrigimos as barras laterais dos detalhes do plantão para acompanhar corretamente a abertura do modal, resolvemos a sobreposição do valor do turno com os botões de ação e removemos um título duplicado, além de mostrar empresa e unidade no upload da folha pré-preenchida.'
      },
      {
        type: 'fix',
        title: 'Pesquisa de satisfação (NPS) mais confiável',
        description:
          'O formulário público passou a orientar o respondente a digitar apenas o nome, e o filtro de empresa para avaliadores mostra o motivo real quando falta permissão em vez de uma lista vazia.'
      }
    ]
  },
  {
    version: '1.10.646',
    date: '2026-09-17',
    range: 'v1.10.593...v1.10.646',
    summary:
      'Nova gestão de localização das unidades no mapa, central de validação de comprovantes com aprovação em lote, evolução da auditoria da folha de ponto e mais mensagens de erro específicas em toda a plataforma.',
    items: [
      {
        type: 'feature',
        title: 'Nova gestão de localização das unidades no mapa',
        description:
          'Cadastro de localização em três passos com busca de endereço e vista de satélite, fila de revisão de sugestões de pin com histórico de alterações e filtro por empresa e estado.'
      },
      {
        type: 'feature',
        title: 'Central de validação de comprovantes com aprovação em lote',
        description:
          'Nova tela permite aprovar comprovantes em lote, reverter aprovações e exportar registros para Excel, além de filtros, ordenação, coluna de verificação e contadores da sessão.'
      },
      {
        type: 'feature',
        title: 'Auditoria da folha de ponto com registro de atividades',
        description:
          'A folha espelho ganhou colunas de assinatura e carimbo, indicador de pagamento, destaque para plantões alterados após a auditoria e um novo registro de atividades com fila unificada.'
      },
      {
        type: 'feature',
        title: 'Central de aprovações com acesso direto ao pós-plantão',
        description:
          'O hub de aprovações passou a reunir a entrada do pós-plantão e exibe o card de exceção antes das trocas feitas pelo aplicativo do médico.'
      },
      {
        type: 'feature',
        title: 'Mais controle sobre a visibilidade da escala',
        description:
          'Novos controles permitem dividir a visibilidade da escala, e a opção de liberar a visão completa também passou a valer no painel web.'
      },
      {
        type: 'feature',
        title: 'Distribuição por grupo nas respostas de NPS',
        description:
          'O detalhe das respostas da pesquisa de satisfação (escala 0 a 10) agora mostra a distribuição das notas por grupo.'
      },
      {
        type: 'feature',
        title: 'Exportação de tentativas de check-in/check-out',
        description:
          'As tentativas de check-in/check-out do plantão podem ser exportadas para Excel e consultadas direto no modal de detalhes do plantão.'
      },
      {
        type: 'fix',
        title: 'Mais mensagens de erro específicas em toda a plataforma',
        description:
          'Escala, qualificação, política de pagamento, unidades de saúde e usuários de plantão agora mostram o motivo real de cada falha em vez de mensagens genéricas.'
      },
      {
        type: 'fix',
        title: 'Cálculo e filtros do NPS mais precisos',
        description:
          'A nota geral do NPS deixa de considerar avaliadores sem base de comparação, e os filtros de período e de relatório ficaram consistentes com o restante do painel.'
      }
    ]
  },
  {
    version: '1.10.593',
    date: '2026-09-08',
    range: 'v1.10.533...v1.10.593',
    summary:
      'Central de aprovações reorganizada em hub, cadastro de unidades de saúde reformulado, regras de aprovação com Análise de Risco vinculada, novidades na pesquisa de satisfação (NPS) e mensagens de erro mais claras em toda a plataforma.',
    items: [
      {
        type: 'feature',
        title: 'Central de aprovações reorganizada em hub de filas',
        description:
          'A tela de aprovações agora é um hub com um cartão para cada fila, facilitando encontrar o que precisa ser aprovado.'
      },
      {
        type: 'feature',
        title: 'Análise de Risco vinculada à Operação nas regras de aprovação',
        description:
          'Ao configurar o fluxo de exceção, a Análise de Risco agora é exigida e travada junto com a Operação, evitando configurações inconsistentes entre as duas etapas.'
      },
      {
        type: 'feature',
        title: 'Cadastro de unidades de saúde reformulado',
        description:
          'Formulário, lista e página de detalhes foram redesenhados: nova etapa de localização, cartões de listagem renovados, máscaras de CEP/CNPJ/telefone e mensagens de validação em português.'
      },
      {
        type: 'feature',
        title: 'Horas consecutivas com escopo por equipe e opção de 18h',
        description:
          'A regra de horas consecutivas agora pode ser configurada por equipe, e os seletores de jornada ganharam a opção de 18 horas.'
      },
      {
        type: 'feature',
        title: 'Novidades na pesquisa de satisfação (NPS)',
        description:
          'Banner de marca no QR code para impressão, prévia fiel do pôster antes de baixar, tela de carregamento na pesquisa pública, barra fixa com o médico selecionado, opção de exigir apenas a pergunta principal e duplicar pesquisas na listagem.'
      },
      {
        type: 'fix',
        title: 'Mensagens de erro mais claras em toda a plataforma',
        description:
          'Erros de rede agora mostram o motivo real da falha em vez de mensagens genéricas ou listas vazias, incluindo aviso de sessão expirada e falhas em formulários de unidades e plantões.'
      },
      {
        type: 'fix',
        title: 'Lista completa de unidades de saúde em filtros, relatórios e plantões',
        description:
          'Unidades de saúde deixam de ser cortadas por paginação em filtros e relatórios, e plantões de unidades inativas voltam a aparecer corretamente.'
      }
    ]
  },
  {
    version: '1.10.533',
    date: '2026-08-26',
    range: 'v1.10.519...v1.10.533',
    summary:
      'Mais bloqueios de segurança no pós-plantão, exportação de relatórios em PDF, melhorias na pesquisa pública de NPS e ações de unidade reorganizadas.',
    items: [
      {
        type: 'feature',
        title: 'Mais bloqueios para plantões já aprovados no pós-plantão',
        description:
          'A trava que já impedia editar um plantão aprovado no pós-plantão agora também alcança novas solicitações de exceção, aprovar/rejeitar/cancelar/reverter uma exceção, remover o médico alocado, excluir plantões em massa e alterar a política de repasse — sempre explicando o motivo em vez de um erro genérico.'
      },
      {
        type: 'feature',
        title: 'Exportar plantões em PDF com página de resumo',
        description:
          'Ao exportar o relatório de plantões, agora é possível escolher entre PDF (com página de resumo e links de navegação) ou ZIP.'
      },
      {
        type: 'feature',
        title: 'Busca de médico na pesquisa pública de NPS',
        description:
          'Unidades com mais de seis médicos ganham um campo de busca por nome, especialidade ou CRM na pesquisa pública, facilitando encontrar quem atendeu o paciente.'
      },
      {
        type: 'feature',
        title: 'Ações da unidade centralizadas na página de detalhes',
        description:
          'Ativar, desativar e editar uma unidade de saúde agora acontece direto na página de detalhes, não mais pela listagem.'
      },
      {
        type: 'improvement',
        title: 'Nota da pergunta principal no modal de respostas do NPS',
        description:
          'O modal de respostas da pesquisa agora mostra a nota da pergunta marcada como principal, em vez de uma média genérica.'
      },
      {
        type: 'fix',
        title: 'Prevenção de votos duplicados na pesquisa pública de NPS',
        description:
          'Um mesmo respondente não consegue mais registrar dois votos na mesma pesquisa pública.'
      }
    ]
  },
  {
    version: '1.10.519',
    date: '2026-08-24',
    range: 'v1.10.471...v1.10.519',
    summary:
      'Nova planilha para digitação e conferência de plantões, gestão de múltiplos CRMs de médicos, personalização das pesquisas de satisfação (NPS) e novas políticas de qualificação documental.',
    items: [
      {
        type: 'feature',
        title: 'Planilha de digitação e auditoria de plantões',
        description:
          'Nova planilha com validação automática por coluna, cole múltiplos dados de uma vez, atalhos de teclado, sugestões de médicos cadastrados, desfaça e refaça alterações em múltiplas etapas, e exporte o relatório em PDF.'
      },
      {
        type: 'feature',
        title: 'Múltiplos CRMs no cadastro do médico',
        description:
          'A página de detalhes do médico agora lista todos os CRMs cadastrados e permite adicionar ou transferir um CRM diretamente.'
      },
      {
        type: 'feature',
        title: 'Personalização das pesquisas de satisfação (NPS)',
        description:
          'Edite a aparência da pesquisa (logo e cores), marque a pergunta principal do formulário, e a identificação do respondente é capturada automaticamente na pesquisa pública.'
      },
      {
        type: 'feature',
        title: 'Políticas de qualificação documental',
        description:
          'Nova gestão de políticas de qualificação por documentação, com vínculo automático ao duplicar as configurações de uma escala existente.'
      },
      {
        type: 'improvement',
        title: 'Horário do plantão na tabela de handover',
        description:
          'A tabela de plantões na tela de handover agora mostra o intervalo de horário de cada plantão.'
      }
    ]
  },
  {
    version: '1.10.471',
    date: '2026-08-10',
    range: 'v1.10.430...v1.10.471',
    summary:
      'Textos mais claros nos detalhes do plantão, melhorias no painel de conformidade de pós-plantão e acesso de médicos pelo aplicativo.',
    items: [
      {
        type: 'feature',
        title: 'Bloqueio de edição de plantão já aprovado no pós-plantão',
        description:
          'Um plantão que já teve sua avaliação de pós-plantão aprovada não pode mais ser editado, evitando inconsistências entre a avaliação e os dados do plantão.'
      },
      {
        type: 'feature',
        title: 'Check-in e check-out automáticos em plantões sequenciais',
        description:
          'Ao encadear plantões em sequência, a saída do plantão anterior e a entrada do próximo agora acontecem automaticamente.'
      },
      {
        type: 'feature',
        title: 'Nova página para links inválidos',
        description:
          'Um link inexistente agora leva a uma página mais clara, com layout adaptado conforme o usuário esteja ou não autenticado.'
      },
      {
        type: 'feature',
        title: 'Políticas de habilitação copiadas ao duplicar configurações de escala',
        description:
          'Ao importar as configurações de uma escala existente para uma nova, as políticas de habilitação já vinculadas também são copiadas automaticamente.'
      },
      {
        type: 'improvement',
        title: 'Detalhes do plantão: fim do "N/A" e dos campos em branco',
        description:
          'Onde antes aparecia "N/A" ou um campo vazio, agora o motivo está escrito. O check-out de um plantão em andamento mostra "Aguardando" e o de um plantão já encerrado sem registro mostra "Não registrado"; o valor de um plantão sem política de pagamento aplicável mostra "Não calculado"; posição, turno e modalidade de repasse não vinculados mostram "Não definido". A evidência ausente passou a dizer "Sem evidência", a seção de observações vazia deixou de desaparecer da tela, e um médico sem CRM cadastrado não deixa mais um traço solto sob o nome.'
      },
      {
        type: 'improvement',
        title: 'Painel de conformidade de pós-plantão mais claro e sempre atualizado',
        description:
          'O registro de atividades foi organizado em um acordeão que se ajusta ao andamento do plantão, o aviso de avaliação revertida ficou mais visível, é possível ver as próximas varreduras a partir do estado vazio, e o painel se atualiza automaticamente enquanto a aba estiver aberta.'
      },
      {
        type: 'improvement',
        title: 'Acesso de médicos direcionado para o aplicativo',
        description:
          'O cadastro público de médicos pelo site foi substituído por uma página de download do aplicativo, e a recuperação de senha agora orienta o médico a usar o app.'
      },
      {
        type: 'fix',
        title: 'Validação de pesquisas de satisfação (NPS) corrigida',
        description:
          'Ao tentar cadastrar uma pesquisa com sobreposição de datas, a mensagem de erro exibida agora reflete corretamente o motivo informado pelo servidor.'
      },
      {
        type: 'fix',
        title: 'Foto e indicador de NPS corrigidos na validação por biometria facial',
        description:
          'A foto do médico volta a ser exibida corretamente nos cartões de validação, e o anel de NPS agora reflete a nota correta.'
      }
    ]
  },
  {
    version: '1.10.430',
    date: '2026-08-05',
    range: 'v1.10.309...v1.10.430',
    summary:
      'Novo painel de conformidade de pós-plantão, repasse de plantão redesenhado e novas regras de escala.',
    items: [
      {
        type: 'feature',
        title: 'Painel de conformidade de pós-plantão',
        description:
          'Novo painel para acompanhar a validação dos plantões, com histórico de evidências, filtros por status e unidade, alertas de sobreposição na escala e opção de reverter ou revalidar uma avaliação manualmente.'
      },
      {
        type: 'feature',
        title: 'Repasse de plantão redesenhado',
        description:
          'A tela de aprovação de repasses ganhou um novo layout com linha do tempo do fluxo, filtros por status e grupo operacional, e avisos de conflito de agenda ou de horas consecutivas antes de aprovar.'
      },
      {
        type: 'feature',
        title: 'Restrição de horas consecutivas',
        description:
          'Agora é possível configurar um limite de horas consecutivas por plantão, com bloqueio e aviso claro (inclusive para plantões que avançam a madrugada) quando o limite é ultrapassado.'
      },
      {
        type: 'feature',
        title: 'Restrição de troca de plantão por equipe',
        description:
          'As regras de troca de plantão agora podem ser configuradas separadamente para equipes internas e externas.'
      },
      {
        type: 'feature',
        title: 'Duplicar configurações ao criar uma escala',
        description:
          'Ao criar uma nova escala, agora é possível importar automaticamente as configurações de uma escala já existente.'
      },
      {
        type: 'feature',
        title: 'Foto de perfil para unidades de saúde',
        description:
          'A página de detalhes da unidade de saúde agora permite enviar e atualizar sua foto de perfil.'
      },
      {
        type: 'fix',
        title: 'Reenvio de convite a médico que recusou',
        description:
          'Agora é possível reenviar o convite para um médico que havia recusado anteriormente.'
      }
    ]
  },
  {
    version: '1.10.309',
    date: '2026-07-15',
    range: 'v1.10.283...v1.10.309',
    summary: 'Nova tela de Unidades de Saúde.',
    items: [
      {
        type: 'feature',
        title: 'Tela de Unidades de Saúde redesenhada',
        description:
          'Nova listagem de unidades com filtros inline, chips de status, estado vazio e página de detalhes da unidade.'
      },
      {
        type: 'fix',
        title: 'Upload de evidência no celular corrigido',
        description: 'O envio de evidências pelo celular voltou a funcionar corretamente.'
      }
    ]
  },
  {
    version: '1.10.283',
    date: '2026-07-07',
    range: 'v1.10.250...v1.10.283',
    summary: 'Novo módulo de Políticas e melhorias na experiência de médicos.',
    items: [
      {
        type: 'feature',
        title: 'Transferência de habilitação',
        description:
          'Novo módulo de Políticas com listagem, detalhes por vínculo e avaliação de transferências de habilitação.'
      },
      {
        type: 'feature',
        title: 'Indicador NPS nos avatares',
        description:
          'Os avatares dos médicos agora exibem um anel colorido com a nota NPS, visível em todas as telas da aplicação.'
      },
      {
        type: 'improvement',
        title: 'Listagem de médicos reorganizada',
        description:
          'A listagem de médicos agora agrupa os profissionais de forma mais clara e exibe o selo de convite apenas para pendentes.'
      }
    ]
  },
  {
    version: '1.10.250',
    date: '2026-06-24',
    range: 'v1.10.230...v1.10.250',
    summary: 'Página de detalhes do médico e melhorias na experiência do usuário.',
    items: [
      {
        type: 'feature',
        title: 'Página de detalhes do médico',
        description:
          'Cada médico agora tem uma página própria com suas informações reunidas em um só lugar. Clique em um médico na listagem para acessá-la.'
      },
      {
        type: 'improvement',
        title: 'Convite de médico mais seguro',
        description:
          'O formulário de convite passou a validar os dados antes do envio, evitando convites com informações incompletas.'
      }
    ]
  },
  {
    version: '1.10.230',
    date: '2026-06-10',
    summary: 'Foto de perfil e correções de avatar.',
    items: [
      {
        type: 'feature',
        title: 'Foto de perfil',
        description:
          'Agora você pode enviar sua foto de perfil, que passa a aparecer no seu perfil e no cabeçalho da aplicação.'
      }
    ]
  }
]

export type ChangelogItemType = 'feature' | 'improvement' | 'fix'

export interface ChangelogItem {
  type: ChangelogItemType
  title: string
  description?: string
}

export interface ChangelogEntry {
  version: string
  date: string
  range?: string
  summary?: string
  items: ChangelogItem[]
}

export const CHANGELOG_ITEM_META: Record<ChangelogItemType, { label: string; icon: string }> = {
  feature: { label: 'Novidade', icon: mdiStarOutline },
  improvement: { label: 'Melhoria', icon: mdiTuneVariant },
  fix: { label: 'Correção', icon: mdiWrenchOutline }
}
